import { useHingeHealthSecurityContext } from '@hinge-health/react-component-library';
import { SecurityContext } from '@hinge-health/react-component-library/dist/components/auth/hinge-health-security-context';
import { useMemo } from 'react';
import * as t from './types';

type AuthClient = SecurityContext['hingeHealthAuthClient'];

const BASE_PATH = '/hinge-select';

const HINGE_SELECT_EXTERNAL_IDENTIFIER = 'care-team-tools-shell-app';

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

// Stage-based write lock. The wizard is read-only on every step except
// `review`; this guard turns the convention into a runtime invariant so a
// future bug that wires a create-* call into a non-review page fails fast
// instead of silently writing. WizardShell flips the flag on each route
// change, and any POST issued while the flag is false rejects with
// WritesBlockedError.
export class WritesBlockedError extends Error {
  constructor(path: string, reason: string) {
    super(`Writes blocked on ${path}: ${reason}`);
    this.name = 'WritesBlockedError';
  }
}

let writesAllowed = false;
let writesBlockedReason = 'Wizard is not on the Review step';

export const setWritesAllowed = (allowed: boolean, reason?: string): void => {
  writesAllowed = allowed;
  if (!allowed) {
    writesBlockedReason = reason ?? 'Wizard is not on the Review step';
  }
};

const baseUrl = (): string => {
  const root = process.env.REACT_APP_BFF_ROOT ?? '';
  return root.replace(/\/+$/, '') + BASE_PATH;
};

const buildHeaders = async (client: AuthClient): Promise<HeadersInit> => {
  const token = await client.getOrRenewAccessToken();
  if (!token) {
    try {
      await client.signOut();
    } catch {
      /* ignore */
    }
    throw new Error('UNAUTHENTICATED: Token expired and could not be renewed');
  }
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const flatten = (
  value: unknown,
  prefix: string,
  out: URLSearchParams,
): void => {
  if (value == null) return;
  if (Array.isArray(value)) {
    value.forEach(v => flatten(v, prefix, out));
    return;
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) {
      flatten(v, prefix ? `${prefix}.${k}` : k, out);
    }
    return;
  }
  out.append(prefix, String(value));
};

const toQueryString = (params: Record<string, unknown>): string => {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    flatten(v, k, usp);
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
};

const httpRequest = async <TRes>(
  client: AuthClient,
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<TRes> => {
  const headers = await buildHeaders(client);
  const res = await fetch(`${baseUrl()}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      /* response not JSON */
    }
    throw new HttpError(res.status, `HTTP ${res.status} on ${path}`, parsed);
  }
  // 204-style responses: BFF link/update calls return {} but always with a JSON body.
  return (await res.json()) as TRes;
};

const toResolveResult = async <TRes>(
  promise: Promise<TRes>,
): Promise<t.ResolveResult<TRes>> => {
  try {
    const data = await promise;
    return { ok: true, data };
  } catch (e) {
    if (e instanceof HttpError) {
      if (e.status === 404) return { ok: false, reason: 'NotFound' };
      if (e.status === 409) return { ok: false, reason: 'Conflict' };
      if (e.status === 410) return { ok: false, reason: 'Terminated' };
    }
    throw e;
  }
};

export const manualSourceTracking = (): t.SourceTracking => ({
  source: { sourceType: t.Source.MANUAL },
  externalIdentifier: HINGE_SELECT_EXTERNAL_IDENTIFIER,
});

const get = <TRes>(
  client: AuthClient,
  path: string,
  query: Record<string, unknown>,
): Promise<TRes> =>
  httpRequest<TRes>(client, 'GET', `${path}${toQueryString(query)}`);

const post = <TRes>(
  client: AuthClient,
  path: string,
  body: unknown,
): Promise<TRes> => {
  if (!writesAllowed) {
    return Promise.reject(new WritesBlockedError(path, writesBlockedReason));
  }
  return httpRequest<TRes>(client, 'POST', path, body);
};

export interface HingeSelectClient {
  // KG: procedure codes & items
  resolveProcedureCodeToItem: (
    req: t.ResolveProcedureCodeToItemRequest,
  ) => Promise<t.ResolveResult<t.ResolveProcedureCodeToItemResponse>>;
  createProcedureCode: (
    req: t.CreateProcedureCodeRequest,
  ) => Promise<t.CreateProcedureCodeResponse>;
  createItem: (req: t.CreateItemRequest) => Promise<t.CreateItemResponse>;
  // Used by the "Global Fees by Scan Type" flow to resolve descriptions to
  // Item.ids. The KG side has no server-side name filter, so callers paginate
  // and match locally.
  listItems: (req: t.ListItemsRequest) => Promise<t.ListItemsResponse>;
  getItem: (req: t.GetItemRequest) => Promise<t.GetItemResponse>;
  linkProcedureCodeToItem: (
    req: t.LinkProcedureCodeToItemRequest,
  ) => Promise<t.LinkProcedureCodeToItemResponse>;

  // KG: bundles
  listBundles: (req: t.ListBundlesRequest) => Promise<t.ListBundlesResponse>;
  createBundle: (req: t.CreateBundleRequest) => Promise<t.CreateBundleResponse>;
  getBundleAndItems: (
    req: t.GetBundleAndItemsRequest,
  ) => Promise<t.GetBundleAndItemsResponse>;

  // KG: merchants
  resolveMerchantByTin: (
    req: t.ResolveMerchantByTinRequest,
  ) => Promise<t.ResolveResult<t.ResolveMerchantByTinResponse>>;
  createMerchant: (
    req: t.CreateMerchantRequest,
  ) => Promise<t.CreateMerchantResponse>;

  // KG: merchant locations
  resolveMerchantLocation: (
    req: t.ResolveMerchantLocationRequest,
  ) => Promise<t.ResolveResult<t.ResolveMerchantLocationResponse>>;
  createMerchantLocation: (
    req: t.CreateMerchantLocationRequest,
  ) => Promise<t.CreateMerchantLocationResponse>;
  linkMerchantLocationToMerchant: (
    req: t.LinkMerchantLocationToMerchantRequest,
  ) => Promise<t.LinkMerchantLocationToMerchantResponse>;
  listMerchantLocationsForMerchant: (
    req: t.ListMerchantLocationsForMerchantRequest,
  ) => Promise<t.ListMerchantLocationsForMerchantResponse>;

  // KG: provider individuals & access points
  resolveProviderIndividual: (
    req: t.ResolveProviderIndividualRequest,
  ) => Promise<t.ResolveResult<t.ResolveProviderIndividualResponse>>;
  createProviderIndividual: (
    req: t.CreateProviderIndividualRequest,
  ) => Promise<t.CreateProviderIndividualResponse>;
  resolveAccessPoint: (
    req: t.ResolveAccessPointRequest,
  ) => Promise<t.ResolveResult<t.ResolveAccessPointResponse>>;
  createAccessPoint: (
    req: t.CreateAccessPointRequest,
  ) => Promise<t.CreateAccessPointResponse>;

  // Legal Entity: organization
  getOrganization: (
    req: t.GetOrganizationRequest,
  ) => Promise<t.ResolveResult<t.GetOrganizationResponse>>;
  createOrganization: (
    req: t.CreateOrganizationRequest,
  ) => Promise<t.CreateOrganizationResponse>;

  // Customer Accounts: provider account
  getProviderAccount: (
    req: t.GetProviderAccountRequest,
  ) => Promise<t.ResolveResult<t.GetProviderAccountResponse>>;
  createProviderAccount: (
    req: t.CreateProviderAccountRequest,
  ) => Promise<t.CreateProviderAccountResponse>;

  // Provider Network: fee schedules
  listFeeSchedules: (
    req: t.ListFeeSchedulesRequest,
  ) => Promise<t.ListFeeSchedulesResponse>;
  createFeeSchedule: (
    req: t.CreateFeeScheduleRequest,
  ) => Promise<t.CreateFeeScheduleResponse>;
  getFeeSchedule: (
    req: t.GetFeeScheduleRequest,
  ) => Promise<t.GetFeeScheduleResponse>;
  updateFeeScheduleState: (
    req: t.UpdateFeeScheduleStateRequest,
  ) => Promise<t.UpdateFeeScheduleStateResponse>;

  // Provider Network: contracts
  listContractsForCustomerAccount: (
    req: t.ListContractsForCustomerAccountRequest,
  ) => Promise<t.ListContractsForCustomerAccountResponse>;
  createContract: (
    req: t.CreateContractRequest,
  ) => Promise<t.CreateContractResponse>;
  getContract: (req: t.GetContractRequest) => Promise<t.GetContractResponse>;
  getContractHistory: (
    req: t.GetContractHistoryRequest,
  ) => Promise<t.GetContractHistoryResponse>;
  updateContract: (
    req: t.UpdateContractRequest,
  ) => Promise<t.UpdateContractResponse>;
  addFeeScheduleToContract: (
    req: t.AddFeeScheduleToContractRequest,
  ) => Promise<t.AddFeeScheduleToContractResponse>;

  // Provider Network: contract service providers
  addContractServiceProviders: (
    req: t.AddContractServiceProvidersRequest,
  ) => Promise<t.AddContractServiceProvidersResponse>;
  getContractServiceProviders: (
    req: t.GetContractServiceProvidersRequest,
  ) => Promise<t.GetContractServiceProvidersResponse>;
  getFeeScheduleForServiceProvider: (
    req: t.GetFeeScheduleForServiceProviderRequest,
  ) => Promise<t.ResolveResult<t.GetFeeScheduleForServiceProviderResponse>>;
}

export const createHingeSelectClient = (
  authClient: AuthClient,
): HingeSelectClient => ({
  resolveProcedureCodeToItem: req =>
    toResolveResult(
      get<t.ResolveProcedureCodeToItemResponse>(
        authClient,
        '/procedure-codes/resolve-to-item',
        req as unknown as Record<string, unknown>,
      ),
    ),
  createProcedureCode: req =>
    post<t.CreateProcedureCodeResponse>(authClient, '/procedure-codes', req),
  createItem: req => post<t.CreateItemResponse>(authClient, '/items', req),
  listItems: req =>
    get<t.ListItemsResponse>(authClient, '/items/list', {
      ...req.paginationParams,
    }),
  getItem: req =>
    get<t.GetItemResponse>(
      authClient,
      '/items/get',
      req as unknown as Record<string, unknown>,
    ),
  linkProcedureCodeToItem: req =>
    post<t.LinkProcedureCodeToItemResponse>(
      authClient,
      '/procedure-codes/link-to-item',
      req,
    ),

  listBundles: req =>
    get<t.ListBundlesResponse>(authClient, '/bundles/list', {
      ...req.paginationParams,
    }),
  createBundle: req =>
    post<t.CreateBundleResponse>(authClient, '/bundles', req),
  getBundleAndItems: req =>
    get<t.GetBundleAndItemsResponse>(
      authClient,
      '/bundles/get-with-items',
      req as unknown as Record<string, unknown>,
    ),

  resolveMerchantByTin: req =>
    toResolveResult(
      get<t.ResolveMerchantByTinResponse>(
        authClient,
        '/merchants/resolve-by-tin',
        req as unknown as Record<string, unknown>,
      ),
    ),
  createMerchant: req =>
    post<t.CreateMerchantResponse>(authClient, '/merchants', req),

  resolveMerchantLocation: req =>
    toResolveResult(
      get<t.ResolveMerchantLocationResponse>(
        authClient,
        '/merchant-locations/resolve',
        req as unknown as Record<string, unknown>,
      ),
    ),
  createMerchantLocation: req =>
    post<t.CreateMerchantLocationResponse>(
      authClient,
      '/merchant-locations',
      req,
    ),
  linkMerchantLocationToMerchant: req =>
    post<t.LinkMerchantLocationToMerchantResponse>(
      authClient,
      '/merchant-locations/link-to-merchant',
      req,
    ),
  listMerchantLocationsForMerchant: async (
    req,
  ): Promise<t.ListMerchantLocationsForMerchantResponse> => {
    // Proto3 JSON collapses empty repeated fields to absent, so an empty
    // result is `{}` on the wire. Normalize to a guaranteed array before
    // returning so consumers never see undefined.
    const raw = await get<{ merchantLocations?: t.MerchantLocation[] }>(
      authClient,
      '/merchant-locations/list-for-merchant',
      req as unknown as Record<string, unknown>,
    );
    return { merchantLocations: raw.merchantLocations ?? [] };
  },

  resolveProviderIndividual: req =>
    toResolveResult(
      get<t.ResolveProviderIndividualResponse>(
        authClient,
        '/provider-individuals/resolve',
        req as unknown as Record<string, unknown>,
      ),
    ),
  createProviderIndividual: req =>
    post<t.CreateProviderIndividualResponse>(
      authClient,
      '/provider-individuals',
      req,
    ),
  resolveAccessPoint: req =>
    toResolveResult(
      get<t.ResolveAccessPointResponse>(
        authClient,
        '/access-points/resolve',
        req as unknown as Record<string, unknown>,
      ),
    ),
  createAccessPoint: req =>
    post<t.CreateAccessPointResponse>(authClient, '/access-points', req),

  getOrganization: req =>
    toResolveResult(
      get<t.GetOrganizationResponse>(
        authClient,
        '/organizations/get',
        req.organizationIdentifier as Record<string, unknown>,
      ),
    ),
  createOrganization: req =>
    post<t.CreateOrganizationResponse>(authClient, '/organizations', req),

  getProviderAccount: req =>
    toResolveResult(
      get<t.GetProviderAccountResponse>(
        authClient,
        '/provider-accounts/get',
        req.providerAccountIdentifier as Record<string, unknown>,
      ),
    ),
  createProviderAccount: req =>
    post<t.CreateProviderAccountResponse>(
      authClient,
      '/provider-accounts',
      req,
    ),

  listFeeSchedules: req =>
    get<t.ListFeeSchedulesResponse>(authClient, '/fee-schedules/list', {
      contractId: req.contractId,
      feeScheduleStates: req.feeScheduleStates,
      ...req.paginationParams,
    }),
  createFeeSchedule: req =>
    post<t.CreateFeeScheduleResponse>(authClient, '/fee-schedules', req),
  getFeeSchedule: req =>
    get<t.GetFeeScheduleResponse>(
      authClient,
      '/fee-schedules/get',
      req as unknown as Record<string, unknown>,
    ),
  updateFeeScheduleState: req =>
    post<t.UpdateFeeScheduleStateResponse>(
      authClient,
      '/fee-schedules/update-state',
      req,
    ),

  listContractsForCustomerAccount: req =>
    get<t.ListContractsForCustomerAccountResponse>(
      authClient,
      '/contracts/list-for-customer-account',
      req as unknown as Record<string, unknown>,
    ),
  createContract: req =>
    post<t.CreateContractResponse>(authClient, '/contracts', req),
  getContract: req =>
    get<t.GetContractResponse>(
      authClient,
      '/contracts/get',
      req as unknown as Record<string, unknown>,
    ),
  getContractHistory: req =>
    get<t.GetContractHistoryResponse>(
      authClient,
      '/contracts/get-history',
      req as unknown as Record<string, unknown>,
    ),
  updateContract: req =>
    post<t.UpdateContractResponse>(authClient, '/contracts/update', req),
  addFeeScheduleToContract: req =>
    post<t.AddFeeScheduleToContractResponse>(
      authClient,
      '/contracts/add-fee-schedule',
      req,
    ),

  addContractServiceProviders: req =>
    post<t.AddContractServiceProvidersResponse>(
      authClient,
      '/contracts/add-service-providers',
      req,
    ),
  getContractServiceProviders: req =>
    get<t.GetContractServiceProvidersResponse>(
      authClient,
      '/contracts/get-service-providers',
      req as unknown as Record<string, unknown>,
    ),
  getFeeScheduleForServiceProvider: req =>
    toResolveResult(
      get<t.GetFeeScheduleForServiceProviderResponse>(
        authClient,
        '/service-providers/get-fee-schedule',
        req as unknown as Record<string, unknown>,
      ),
    ),
});

export const useHingeSelectClient = (): HingeSelectClient => {
  const { hingeHealthAuthClient } = useHingeHealthSecurityContext();
  return useMemo(
    () => createHingeSelectClient(hingeHealthAuthClient),
    [hingeHealthAuthClient],
  );
};
